import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, RefreshCw, Sparkles, TriangleAlert, X } from 'lucide-react';

import { dataApi } from '../../services/api';
import appwrite, { COLLECTIONS } from '../../services/appwrite';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Combobox } from '../ui/combobox';
import { Skeleton } from '../ui/skeleton';
import { Tooltip } from '../ui/tooltip';
import CollapseExpandButton from '../ui/collapse-expand-button';

/**
 * Maps PanelGear insurers to Panel Quote insurance customers.
 *
 * Panel Quote bills an insurance job to the insurer, so this mapping is what
 * decides who gets invoiced when a job is handed over. Nothing here saves on
 * its own: AI proposals are staged next to the insurer they belong to and only
 * become real when an assessor presses Save, because a wrong mapping sends the
 * job to the wrong company without anyone noticing.
 */

const CONFIDENCE_VARIANT = { high: 'default', medium: 'secondary', low: 'outline' };

/**
 * Both server actions are rate limited to one run every two minutes, matching
 * the cooldown on pushing a quote to Panel Quote. The server is the authority —
 * this only keeps the buttons honest about it.
 */
const ACTION_COOLDOWN_MS = 2 * 60 * 1000;

const relativeTime = (iso) => {
  if (!iso) return 'Never';
  const diffMins = (Date.now() - new Date(iso).getTime()) / 60000;
  if (!Number.isFinite(diffMins)) return 'Never';
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${Math.floor(diffMins)}m ago`;
  if (diffMins < 60 * 24) return `${Math.floor(diffMins / 60)}h ago`;
  const days = Math.floor(diffMins / (60 * 24));
  return `${days} day${days === 1 ? '' : 's'} ago`;
};

/** Seconds left on a cooldown, or 0 when it has expired. */
const cooldownRemaining = (iso, now) => {
  if (!iso) return 0;
  const elapsed = now - new Date(iso).getTime();
  if (!Number.isFinite(elapsed) || elapsed >= ACTION_COOLDOWN_MS) return 0;
  return Math.ceil((ACTION_COOLDOWN_MS - elapsed) / 1000);
};

const PanelQuoteInsurerMapping = ({ enabled }) => {
  const [expanded, setExpanded] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [insurers, setInsurers] = useState([]);
  const [pqCustomers, setPqCustomers] = useState([]);
  const [mappings, setMappings] = useState({});
  const [syncStatus, setSyncStatus] = useState(null);
  const [draft, setDraft] = useState({});
  const [syncing, setSyncing] = useState(false);
  const [automapping, setAutomapping] = useState(false);
  const [saving, setSaving] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [insurerList, customerList, mappingList, status] = await Promise.all([
        appwrite.getInsurers(),
        dataApi.panelQuoteCachedCustomers('insurance'),
        dataApi.panelQuoteInsurerMappings(),
        dataApi.panelQuoteSyncStatusGet()
      ]);

      setInsurers(insurerList.filter((i) => i.active !== false));
      setPqCustomers(customerList);
      setMappings(Object.fromEntries(mappingList.map((m) => [m.insurerId, m])));
      setSyncStatus(status);
      setLoaded(true);
    } catch (error) {
      console.error('Error loading Panel Quote insurer mapping:', error);
      toast.error('Failed to load the Panel Quote insurer mapping');
    } finally {
      setLoading(false);
    }
  }, []);

  // Nothing is fetched until the section is opened — the address book is a few
  // hundred rows nobody asked for while they are editing credentials.
  useEffect(() => {
    if (enabled && expanded && !loaded) load();
  }, [enabled, expanded, loaded, load]);

  // The address-book sync runs in the background, so the status row is what
  // tells us it finished — and when it does, the customer list has changed
  // underneath us.
  useEffect(() => {
    if (!enabled || !expanded) return undefined;
    let cancelled = false;
    let unsubscribe;

    (async () => {
      const user = await appwrite.getCurrentUser();
      if (!user?.teamId || cancelled) return;
      unsubscribe = appwrite.subscribeToDocument(COLLECTIONS.PANEL_QUOTE_STATUS, user.teamId, (event) => {
        const row = event?.payload;
        if (!row) return;
        setSyncStatus(row);
        if (!row.customersSyncing) {
          setSyncing(false);
          if (row.customersSyncStatus === 'error') {
            toast.error(row.customersSyncError || 'Panel Quote address book sync failed');
          } else if (row.customersSyncStatus === 'success') {
            dataApi.panelQuoteCachedCustomers('insurance').then(setPqCustomers).catch(() => {});
            toast.success('Panel Quote address book synced');
          }
        }
      });
    })();

    return () => {
      cancelled = true;
      if (unsubscribe) unsubscribe();
    };
  }, [enabled, expanded]);

  const syncCooldown = cooldownRemaining(syncStatus?.customersLastSynced, now);
  const automapCooldown = cooldownRemaining(syncStatus?.insurersLastAutoMapped, now);

  // Tick only while a cooldown is actually running, so the buttons re-enable the
  // moment they expire without a timer running for the life of the page.
  useEffect(() => {
    if (!expanded || (syncCooldown === 0 && automapCooldown === 0)) return undefined;
    const timer = setTimeout(() => setNow(Date.now()), 1000);
    return () => clearTimeout(timer);
  }, [expanded, syncCooldown, automapCooldown, now]);

  // The sync is fire-and-forget, so the status row is the only thing that can
  // tell us it started. If the server rejected the run outright — rate limited,
  // credentials gone, cold start failed — nothing is ever written and the button
  // would sit on "Syncing…" forever. Give up waiting after 30 seconds.
  useEffect(() => {
    if (!syncing || syncStatus?.customersSyncing) return undefined;
    const timer = setTimeout(() => {
      setSyncing(false);
      toast.error('The address book sync did not start. Check the Panel Quote credentials and try again.');
    }, 30000);
    return () => clearTimeout(timer);
  }, [syncing, syncStatus?.customersSyncing]);

  const customerOptions = useMemo(() => pqCustomers.map((c) => ({
    value: c.panelQuoteCustomerId,
    label: c.name,
    description: c.isFavourite ? 'Favourite' : undefined
  })), [pqCustomers]);

  const customersById = useMemo(
    () => new Map(pqCustomers.map((c) => [c.panelQuoteCustomerId, c])),
    [pqCustomers]
  );

  const pendingCount = Object.keys(draft).length;
  const mappedCount = insurers.filter((i) => mappings[i.$id] || draft[i.$id]).length;

  const handleSync = async () => {
    if (syncCooldown > 0) {
      toast.info(`The address book was synced less than two minutes ago — try again in ${syncCooldown}s`);
      return;
    }
    try {
      setSyncing(true);
      await dataApi.panelQuoteSyncCustomers();
      toast.info('Syncing the Panel Quote address book…');
    } catch (error) {
      setSyncing(false);
      toast.error(`Could not start the sync: ${error.message}`);
    }
  };

  const handleAutoMap = async () => {
    try {
      setAutomapping(true);
      const result = await dataApi.panelQuoteAutoMapInsurers();
      const proposals = result?.proposals || [];
      // The server stamps the run, so reflect the new cooldown immediately.
      dataApi.panelQuoteSyncStatusGet().then(setSyncStatus).catch(() => {});

      if (proposals.length === 0) {
        toast.info(
          result?.alreadyMapped === insurers.length
            ? 'Every insurer is already mapped'
            : 'No confident matches found — map the remaining insurers by hand'
        );
        return;
      }

      setDraft((prev) => {
        const next = { ...prev };
        for (const proposal of proposals) {
          next[proposal.insurerId] = {
            panelQuoteCustomerId: proposal.panelQuoteCustomerId,
            panelQuoteCustomerName: proposal.panelQuoteCustomerName,
            source: 'ai',
            confidence: proposal.confidence,
            reason: proposal.reason
          };
        }
        return next;
      });

      const unmatched = result?.unmatched?.length || 0;
      toast.success(
        `${proposals.length} suggestion${proposals.length === 1 ? '' : 's'} ready for review`
        + (unmatched ? ` — ${unmatched} could not be matched` : '')
      );
    } catch (error) {
      console.error('Auto-map failed:', error);
      toast.error(`Auto-map failed: ${error.message}`);
    } finally {
      setAutomapping(false);
    }
  };

  const handlePick = (insurer, panelQuoteCustomerId) => {
    setDraft((prev) => {
      const next = { ...prev };
      const existing = mappings[insurer.$id];

      if (!panelQuoteCustomerId) {
        // Clearing something already saved is a change; clearing an unsaved
        // suggestion just drops the suggestion.
        if (existing) next[insurer.$id] = { panelQuoteCustomerId: '', source: 'manual', confidence: '' };
        else delete next[insurer.$id];
        return next;
      }

      if (existing?.panelQuoteCustomerId === panelQuoteCustomerId) {
        delete next[insurer.$id];
        return next;
      }

      next[insurer.$id] = {
        panelQuoteCustomerId,
        panelQuoteCustomerName: customersById.get(panelQuoteCustomerId)?.name || '',
        source: 'manual',
        confidence: ''
      };
      return next;
    });
  };

  const handleSave = async () => {
    const entries = Object.entries(draft);
    if (entries.length === 0) return;

    setSaving(true);
    const insurerById = new Map(insurers.map((i) => [i.$id, i]));
    const failed = {};
    const failureMessages = [];

    for (const [insurerId, change] of entries) {
      try {
        if (!change.panelQuoteCustomerId) {
          await dataApi.panelQuoteDeleteInsurerMapping(insurerId);
        } else {
          await dataApi.panelQuoteSaveInsurerMapping({
            insurerId,
            insurerName: insurerById.get(insurerId)?.name || '',
            panelQuoteCustomerId: change.panelQuoteCustomerId,
            panelQuoteCustomerName: change.panelQuoteCustomerName,
            source: change.source,
            confidence: change.confidence
          });
        }
      } catch (error) {
        // Whatever failed stays staged. Clearing the whole draft would throw
        // away work the user cannot see anywhere else and would have to redo.
        failed[insurerId] = change;
        failureMessages.push(`${insurerById.get(insurerId)?.name || insurerId}: ${error.message}`);
      }
    }

    try {
      const mappingList = await dataApi.panelQuoteInsurerMappings();
      setMappings(Object.fromEntries(mappingList.map((m) => [m.insurerId, m])));
    } catch (error) {
      console.error('Saved, but could not reload the mappings:', error);
    }

    setDraft(failed);
    setSaving(false);

    const savedCount = entries.length - failureMessages.length;
    if (failureMessages.length > 0) {
      console.error('Some insurer mappings failed to save:', failureMessages);
      toast.error(
        `${failureMessages.length} mapping${failureMessages.length === 1 ? '' : 's'} could not be saved`
        + (savedCount ? ` (${savedCount} saved)` : '')
        + ' — your selections have been kept. ' + failureMessages[0]
      );
    } else {
      toast.success(`Saved ${savedCount} mapping${savedCount === 1 ? '' : 's'}`);
    }
  };

  if (!enabled) return null;

  const isSyncing = syncing || syncStatus?.customersSyncing;
  const hasAddressBook = pqCustomers.length > 0;
  const syncDisabled = isSyncing || syncCooldown > 0;
  const automapDisabled = automapping || !hasAddressBook || automapCooldown > 0;

  return (
    <div id="panel-quote-insurers" className="space-y-4 border-t pt-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold">Insurer Mapping</h3>
          <p className="text-sm text-muted-foreground">
            Insurers are set up as customers in Panel Quote. In order to create jobs in Panel Quote from
            PanelGear, you must first map them.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {expanded && (
            <>
              <Tooltip content={syncCooldown > 0 ? `Rate limited — ${syncCooldown}s to go` : 'Refetch the insurance customers from Panel Quote'}>
                <Button variant="outline" size="sm" onClick={handleSync} disabled={syncDisabled}>
                  {isSyncing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                  {isSyncing ? 'Syncing…' : `Sync address book${syncCooldown > 0 ? ` (${syncCooldown}s)` : ''}`}
                </Button>
              </Tooltip>
              <Tooltip content={automapCooldown > 0 ? `Rate limited — ${automapCooldown}s to go` : 'Suggest a Panel Quote customer for each unmapped insurer'}>
                <Button variant="outline" size="sm" onClick={handleAutoMap} disabled={automapDisabled}>
                  {automapping ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                  {automapping ? 'Matching…' : `Auto-map with AI${automapCooldown > 0 ? ` (${automapCooldown}s)` : ''}`}
                </Button>
              </Tooltip>
              <Button size="sm" onClick={handleSave} disabled={saving || pendingCount === 0}>
                {saving ? 'Saving…' : `Save${pendingCount ? ` (${pendingCount})` : ''}`}
              </Button>
            </>
          )}
          {!expanded && loaded && pendingCount > 0 && (
            <Badge variant="outline">{pendingCount} unsaved</Badge>
          )}
          <CollapseExpandButton isExpanded={expanded} onClick={() => setExpanded((v) => !v)} />
        </div>
      </div>

      {expanded && (
        <>
          <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
            <span>Address book last synced: {relativeTime(syncStatus?.customersLastSynced)}</span>
            <span>{pqCustomers.length} Panel Quote insurance customers</span>
            <span>{mappedCount} of {insurers.length} insurers mapped</span>
          </div>

          {loaded && !hasAddressBook && (
            <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              <TriangleAlert className="h-4 w-4 mt-0.5 shrink-0" />
              <span>
                No Panel Quote customers cached yet. Run <strong>Sync address book</strong> before mapping —
                it pulls your saved insurance customers from Panel Quote.
              </span>
            </div>
          )}

          {loading && !loaded ? (
            <div className="space-y-2">
              {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
            </div>
          ) : (
            <div className="rounded-md border divide-y">
              {insurers.length === 0 && (
                <p className="p-4 text-sm text-muted-foreground">No insurers configured yet.</p>
              )}
              {insurers.map((insurer) => {
                const saved = mappings[insurer.$id];
                const change = draft[insurer.$id];
                const value = change ? change.panelQuoteCustomerId : (saved?.panelQuoteCustomerId || '');
                const pqCustomer = customersById.get(value);
                const isSuggestion = change?.source === 'ai';

                return (
                  <div key={insurer.$id} className="flex flex-wrap items-center gap-3 p-3">
                    <div className="min-w-45 flex-1">
                      <p className="text-sm font-medium">{insurer.name}</p>
                    </div>

                    <div className="min-w-65 flex-1">
                      <Combobox
                        value={value}
                        onChange={(next) => handlePick(insurer, next)}
                        options={customerOptions}
                        placeholder={hasAddressBook ? 'Search Panel Quote customers…' : 'Sync the address book first'}
                        emptyText="No matching Panel Quote customer"
                        disabled={!hasAddressBook}
                      />
                    </div>

                    <div className="flex w-47.5 items-center justify-end gap-2">
                      {isSuggestion && (
                        // The justification lives in the tooltip: it matters when
                        // you question a suggestion, not while you scan the list.
                        <Tooltip
                          content={change.reason
                            || `${pqCustomer?.isFavourite ? 'A Panel Quote favourite. ' : ''}No reason given.`}
                        >
                          <Badge variant={CONFIDENCE_VARIANT[change.confidence] || 'outline'}>
                            Suggested · {change.confidence}
                          </Badge>
                        </Tooltip>
                      )}
                      {!isSuggestion && change && <Badge variant="outline">Unsaved</Badge>}
                      {!change && saved && (
                        <Badge variant="secondary">{saved.source === 'ai' ? 'AI · confirmed' : 'Mapped'}</Badge>
                      )}
                      {value && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => handlePick(insurer, '')}
                          title="Clear this mapping"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PanelQuoteInsurerMapping;
